import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";

// ========================================
// Types for FlipClass Configuration
// ========================================

export interface FlipClassPhaseConfig {
  id: number;
  name: string;
  phase: "pre-class" | "in-class" | "post-class";
  tags: number[];
  description: string;
  sortOrder: number;
  autoAssign: boolean;
}

export interface FlipClassConfigUpdateItem {
  id: number;
  flipClassConfig: {
    phase: "pre-class" | "in-class" | "post-class";
    tags: number[];
    autoAssign: boolean;
    description: string;
  };
}

// ========================================
// Types for Engagement Tracking
// ========================================

export interface EngagementRecord {
  Id: number;
  StepId: number;
  BatchId: number;
  UserId: number;
  EnrollmentId?: number;
  SessionDate: string;
  HandsRaisedCount: number;
  ParticipationLevel: "High" | "Medium" | "Low" | null;
  GroupRole: "Solver" | "Explainer" | "Listener" | "Absent" | null;
  TeacherNotes?: string;
  PollCorrect: number;
  PollTotal: number;
}

export interface EngagementUpdate {
  userId: number;
  enrollmentId?: number;
  handsRaisedCount?: number;
  participationLevel?: "High" | "Medium" | "Low";
  groupRole?: "Solver" | "Explainer" | "Listener" | "Absent";
  teacherNotes?: string;
}

// ========================================
// Types for Flash Quiz
// ========================================

export interface PreQuizQuestion {
  sectionId: number;
  questionId: number;
  questionNumber: number;
  question: string;
  options: string[];
  concept: string;
  difficulty?: string;
  preQuizStats: {
    totalAttempts: number;
    correctCount: number;
    pctCorrect: number | null;
  };
}

export interface FlashQuizSession {
  sessionId: number;
  stepId: number;
  batchId: number;
  testId: number;
  sectionId: number;
  question: string;
  options: string[];
  concept?: string;
  durationSeconds: number;
  startedAt: string;
  isActive?: boolean;
}

export interface FlashQuizResults {
  sessionId: number;
  isActive: boolean;
  question: string;
  concept?: string;
  correctAnswer: string;
  options: string[];
  optionCounts: Record<string, number>;
  totalResponses: number;
  totalStudents: number;
  responseRate: number;
  correctCount: number;
  inClassPctCorrect: number;
  preQuizPctCorrect: number | null;
  improvement: number | null;
  startedAt: string;
  closedAt?: string;
}

export interface ActiveFlashQuiz {
  sessionId: number;
  question: string;
  options: string[];
  concept?: string;
  durationSeconds: number;
  remainingSeconds: number;
  startedAt: string;
  hasResponded: boolean;
}

// ========================================
// Types for In-Class Quiz
// ========================================

export interface InClassQuiz {
  testId: number;
  testName: string;
  questionCount: number;
  tagId?: number;
}

export interface InClassQuizResults {
  testId: number;
  testName: string;
  totalStudents: number;
  completedCount: number;
  completionRate: number;
  avgScore: number;
  hardestQuestion?: {
    question: string;
    concept: string;
    pctCorrect: number;
  };
  easiestQuestion?: {
    question: string;
    concept: string;
    pctCorrect: number;
  };
  questionStats: Array<{
    question: string;
    concept: string;
    attempts: number;
    correctCount: number;
    pctCorrect: number;
  }>;
}

// ========================================
// FlipClassService
// ========================================

class FlipClassService {
  private baseUrl = `${Config.nodeSiteUrl}v1/flip-class`;

  // ========================================
  // Configuration Methods
  // ========================================

  /**
   * Get flip class phase configuration
   */
  async getConfig(): Promise<FlipClassPhaseConfig[]> {
    const response = await myAxios.get(`${this.baseUrl}/config`);
    return response.data as FlipClassPhaseConfig[];
  }

  /**
   * Update flip class phase configuration
   */
  async updateConfig(
    configs: FlipClassConfigUpdateItem[]
  ): Promise<{ success: boolean; message: string; updated: number }> {
    const response = await myAxios.put(`${this.baseUrl}/config`, { configs });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return response.data as any;
  }

  // ========================================
  // Engagement Tracking Methods
  // ========================================

  /**
   * Get all engagement records for a session
   */
  async getEngagementRecords(
    stepId: number,
    batchId: number,
    sessionDate?: string
  ): Promise<{ success: boolean; data: { sessionDate: string; records: EngagementRecord[] } }> {
    const params: Record<string, any> = { stepId, batchId };
    if (sessionDate) params.sessionDate = sessionDate;

    const response = await myAxios.get(`${this.baseUrl}/engagement`, { params });
    return response.data as { success: boolean; data: { sessionDate: string; records: EngagementRecord[] } };
  }

  /**
   * Create or update engagement record for a student
   */
  async upsertEngagementRecord(
    stepId: number,
    batchId: number,
    userId: number,
    data: {
      enrollmentId?: number;
      sessionDate?: string;
      handsRaisedCount?: number;
      participationLevel?: "High" | "Medium" | "Low";
      groupRole?: "Solver" | "Explainer" | "Listener" | "Absent";
      teacherNotes?: string;
    }
  ): Promise<{ success: boolean; data: EngagementRecord }> {
    const response = await myAxios.post(`${this.baseUrl}/engagement`, {
      stepId,
      batchId,
      userId,
      ...data,
    });
    return response.data as unknown as { success: boolean; data: EngagementRecord };
  }

  /**
   * Bulk update engagement records
   */
  async bulkUpdateEngagement(
    stepId: number,
    batchId: number,
    updates: EngagementUpdate[],
    sessionDate?: string
  ): Promise<{ success: boolean; data: { processed: number; results: any[] } }> {
    const response = await myAxios.post(`${this.baseUrl}/engagement/bulk`, {
      stepId,
      batchId,
      sessionDate,
      updates,
    });
    return response.data as unknown as { success: boolean; data: { processed: number; results: any[] } };
  }

  /**
   * Quick increment hands-up count for a student
   */
  async incrementHandsUp(
    stepId: number,
    batchId: number,
    userId: number,
    enrollmentId?: number
  ): Promise<{ success: boolean; data: { id: number; userId: number; handsRaisedCount: number } }> {
    const response = await myAxios.post(`${this.baseUrl}/engagement/hands-up`, {
      stepId,
      batchId,
      userId,
      enrollmentId,
    });
    return response.data as unknown as { success: boolean; data: { id: number; userId: number; handsRaisedCount: number } };
  }

  /**
   * Quick decrement hands-up count for a student (minimum 0)
   */
  async decrementHandsUp(
    stepId: number,
    batchId: number,
    userId: number
  ): Promise<{ success: boolean; data: { id: number; userId: number; handsRaisedCount: number } }> {
    const response = await myAxios.post(`${this.baseUrl}/engagement/hands-down`, {
      stepId,
      batchId,
      userId,
    });
    return response.data as unknown as { success: boolean; data: { id: number; userId: number; handsRaisedCount: number } };
  }

  // ========================================
  // Flash Quiz Methods
  // ========================================

  /**
   * Get available pre-quiz questions to flash
   */
  async getPreQuizQuestions(
    stepId: number,
    batchId?: number
  ): Promise<{ success: boolean; data: { testId: number; testName: string; questions: PreQuizQuestion[] } }> {
    const params: Record<string, any> = { stepId };
    if (batchId) params.batchId = batchId;

    const response = await myAxios.get(`${this.baseUrl}/quiz/questions`, { params });
    return response.data as { success: boolean; data: { testId: number; testName: string; questions: PreQuizQuestion[] } };
  }

  /**
   * Flash a question to the class
   */
  async flashQuestion(
    stepId: number,
    batchId: number,
    testId: number,
    sectionId: number,
    questionId: number,
    durationSeconds: number = 60
  ): Promise<{ success: boolean; data: FlashQuizSession }> {
    const response = await myAxios.post(`${this.baseUrl}/quiz/flash`, {
      stepId,
      batchId,
      testId,
      sectionId,
      questionId,
      durationSeconds,
    });
    return response.data as unknown as { success: boolean; data: FlashQuizSession };
  }

  /**
   * Get currently active flash quiz (for students)
   */
  async getActiveFlashQuiz(
    stepId: number,
    batchId: number
  ): Promise<{ success: boolean; data: ActiveFlashQuiz | null }> {
    const response = await myAxios.get(`${this.baseUrl}/quiz/active`, {
      params: { stepId, batchId },
    });
    return response.data as { success: boolean; data: ActiveFlashQuiz | null };
  }

  /**
   * Submit response to flash quiz
   */
  async submitFlashQuizResponse(
    sessionId: number,
    selectedOption: string,
    enrollmentId?: number,
    responseTimeSeconds?: number
  ): Promise<{ success: boolean; data: { isCorrect: boolean; selectedOption: string } }> {
    const response = await myAxios.post(`${this.baseUrl}/quiz/respond`, {
      sessionId,
      selectedOption,
      enrollmentId,
      responseTimeSeconds,
    });
    return response.data as unknown as { success: boolean; data: { isCorrect: boolean; selectedOption: string } };
  }

  /**
   * Get live results for a flash quiz
   */
  async getFlashQuizResults(sessionId: number): Promise<{ success: boolean; data: FlashQuizResults }> {
    const response = await myAxios.get(`${this.baseUrl}/quiz/results/${sessionId}`);
    return response.data as { success: boolean; data: FlashQuizResults };
  }

  /**
   * Close the flash quiz
   */
  async closeFlashQuiz(
    sessionId: number
  ): Promise<{ success: boolean; data: { sessionId: number; closedAt: string; responsesProcessed: number } }> {
    const response = await myAxios.post(`${this.baseUrl}/quiz/close/${sessionId}`);
    return response.data as unknown as { success: boolean; data: { sessionId: number; closedAt: string; responsesProcessed: number } };
  }

  /**
   * Get all flash quizzes for a session
   */
  async getFlashQuizHistory(
    stepId: number,
    batchId: number
  ): Promise<{ success: boolean; data: Array<FlashQuizSession & { totalResponses: number; correctCount: number; pctCorrect: number }> }> {
    const response = await myAxios.get(`${this.baseUrl}/quiz/history`, {
      params: { stepId, batchId },
    });
    return response.data as { success: boolean; data: Array<FlashQuizSession & { totalResponses: number; correctCount: number; pctCorrect: number }> };
  }

  // ========================================
  // In-Class Quiz Methods
  // ========================================

  /**
   * Get in-class quiz assigned to a step
   */
  async getInClassQuiz(stepId: number): Promise<{ success: boolean; data: InClassQuiz | null }> {
    const response = await myAxios.get(`${this.baseUrl}/step/${stepId}/in-class-quiz`);
    return response.data as { success: boolean; data: InClassQuiz | null };
  }

  /**
   * Assign a quiz to a step as in-class quiz
   */
  async assignInClassQuiz(
    stepId: number,
    testId: number
  ): Promise<{ success: boolean; data: { mappingId: number; testId: number; tagId: number } }> {
    const response = await myAxios.post(`${this.baseUrl}/step/${stepId}/in-class-quiz`, { testId });
    return response.data as unknown as { success: boolean; data: { mappingId: number; testId: number; tagId: number } };
  }

  /**
   * Remove in-class quiz assignment
   */
  async removeInClassQuiz(stepId: number): Promise<{ success: boolean }> {
    const response = await myAxios.delete(`${this.baseUrl}/step/${stepId}/in-class-quiz`);
    return response.data as { success: boolean };
  }

  /**
   * Get in-class quiz results for a batch
   */
  async getInClassQuizResults(
    stepId: number,
    batchId: number
  ): Promise<{ success: boolean; data: InClassQuizResults | null }> {
    const response = await myAxios.get(`${this.baseUrl}/step/${stepId}/in-class-quiz/results`, {
      params: { batchId },
    });
    return response.data as { success: boolean; data: InClassQuizResults | null };
  }

  /**
   * Get available quizzes to assign as in-class quiz
   */
  async getAvailableQuizzes(
    stepId: number
  ): Promise<{ success: boolean; data: Array<{ testId: number; testName: string; questionCount: number }> }> {
    const response = await myAxios.get(`${this.baseUrl}/step/${stepId}/available-quizzes`);
    return response.data as { success: boolean; data: Array<{ testId: number; testName: string; questionCount: number }> };
  }

  // ========================================
  // AI Tweaks Method (already exists, adding for completeness)
  // ========================================

  /**
   * Generate AI-powered in-class tweaks
   */
  async generateTweaks(params: {
    stepId: number;
    batchId: number;
    segment: string;
    context: {
      totalStudents: number;
      unpreparedPct: number;
      avgPreQuizScore: number;
      hardestConcept: string;
      hardestConceptPctCorrect: number;
      atRiskStudents: number;
      lowEngagementStudents: number;
    };
  }): Promise<{
    success: boolean;
    data: {
      tweaks: Array<{
        id: string;
        title: string;
        description: string;
        priority: "high" | "medium" | "low";
        timeEstimate?: string;
      }>;
      overallAssessment: string;
      tokenUsage: { inputTokens: number; outputTokens: number };
      processingTimeMs: number;
    };
  }> {
    const response = await myAxios.post(`${this.baseUrl}/step/generate-tweaks`, params);
    return response.data as unknown as {
      success: boolean;
      data: {
        tweaks: Array<{
          id: string;
          title: string;
          description: string;
          priority: "high" | "medium" | "low";
          timeEstimate?: string;
        }>;
        overallAssessment: string;
        tokenUsage: { inputTokens: number; outputTokens: number };
        processingTimeMs: number;
      };
    };
  }

  /**
   * Generate AI-powered post-class recommendations
   */
  async generatePostClassRecommendations(params: {
    stepId: number;
    batchId: number;
    context: {
      totalStudents: number;
      avgQuizScore: number;
      readyCount: number;
      borderlineCount: number;
      notReadyCount: number;
      improvedCount: number;
      smallGainCount: number;
      noGainCount: number;
      avgPracticeMin: number;
      practicedCount: number;
      recapViewedCount: number;
      reflectionCount: number;
      hardestConcept: string;
      hardestConceptPctCorrect: number;
      easiestConcept?: string;
      easiestConceptPctCorrect?: number;
      notReadyStudentNames?: string[];
    };
  }): Promise<{
    success: boolean;
    data: {
      recommendations: Array<{
        id: string;
        title: string;
        description: string;
        priority: "high" | "medium" | "low";
        targetGroup?: "ready" | "borderline" | "notReady" | "all";
        actionType?: "assign" | "review" | "discuss" | "practice";
      }>;
      overallAssessment: string;
      nextClassFocus: string;
      tokenUsage: { inputTokens: number; outputTokens: number };
      processingTimeMs: number;
    };
  }> {
    const response = await myAxios.post(`${this.baseUrl}/step/generate-post-recommendations`, params);
    return response.data as unknown as {
      success: boolean;
      data: {
        recommendations: Array<{
          id: string;
          title: string;
          description: string;
          priority: "high" | "medium" | "low";
          targetGroup?: "ready" | "borderline" | "notReady" | "all";
          actionType?: "assign" | "review" | "discuss" | "practice";
        }>;
        overallAssessment: string;
        nextClassFocus: string;
        tokenUsage: { inputTokens: number; outputTokens: number };
        processingTimeMs: number;
      };
    };
  }
  /**
   * Compute step scores (PreScore, InScore, PostScore) on demand.
   * @param batchId - optional; omit for institution-wide computation
   */
  async computeStepScores(batchId?: number) {
    const response = await myAxios.post(
      `${this.baseUrl}/executive/compute-scores`,
      batchId ? { batchId } : {},
      { timeout: 120000 },
    );
    return response.data as {
      success: boolean;
      data: {
        batchesProcessed: number;
        stepsUpdated: number;
        errors: string[];
      };
    };
  }

  // ========================================
  // Whiteboard Methods (In-Class Whiteboard)
  // ========================================

  async startWhiteboard(
    stepId: number,
    batchId: number,
  ): Promise<{
    success: boolean;
    data: {
      sessionId: number;
      stepId: number;
      batchId: number;
      currentSeq: number;
      status: string;
      startedAt: string;
    };
  }> {
    const response = await myAxios.post(`${this.baseUrl}/whiteboard/start`, {
      stepId,
      batchId,
    });
    return response.data as any;
  }

  async appendWhiteboardStrokes(
    sessionId: number,
    strokes: Array<{
      type: "stroke" | "highlight";
      color: string;
      width: number;
      points: Array<{ x: number; y: number }>;
    }>,
  ): Promise<{
    success: boolean;
    data: { sessionId: number; currentSeq: number; appended: number };
  }> {
    const response = await myAxios.post(`${this.baseUrl}/whiteboard/strokes`, {
      sessionId,
      strokes,
    });
    return response.data as any;
  }

  async sendWhiteboardControl(
    sessionId: number,
    type: "clear" | "undo",
  ): Promise<{
    success: boolean;
    data: { sessionId: number; currentSeq: number; type: string };
  }> {
    const response = await myAxios.post(`${this.baseUrl}/whiteboard/control`, {
      sessionId,
      type,
    });
    return response.data as any;
  }

  async closeWhiteboard(
    sessionId: number,
  ): Promise<{
    success: boolean;
    data: { sessionId: number; closedAt: string; alreadyClosed?: boolean };
  }> {
    const response = await myAxios.post(
      `${this.baseUrl}/whiteboard/close/${sessionId}`,
    );
    return response.data as any;
  }

  // ========================================
  // Session Group Methods (AI Case Discussions foundation)
  // ========================================

  async suggestGroups(
    stepId: number,
    batchId: number,
    opts?: { groupSize?: number; groupCount?: number; sessionDate?: string },
  ): Promise<{
    success: boolean;
    data: {
      groups: SuggestedGroup[];
      strategy: "balanced_prequiz" | "random_balanced";
      rosterCount: number;
    };
  }> {
    const response = await myAxios.post(
      `${this.baseUrl}/step/${stepId}/groups/suggest`,
      { batchId, ...(opts || {}) },
    );
    return response.data as any;
  }

  async createGroups(
    stepId: number,
    batchId: number,
    groups: Array<{
      name: string;
      members: Array<{ userId: number; enrollmentId?: number; role?: GroupRole }>;
    }>,
    sessionDate?: string,
  ): Promise<{
    success: boolean;
    data: { sessionDate: string; groups: Array<{ id: number; name: string; memberCount: number }> };
  }> {
    const response = await myAxios.post(
      `${this.baseUrl}/step/${stepId}/groups`,
      { batchId, sessionDate, groups },
    );
    return response.data as any;
  }

  async listGroups(
    stepId: number,
    batchId: number,
    sessionDate?: string,
  ): Promise<{
    success: boolean;
    data: { sessionDate: string; groups: SessionGroupRecord[] };
  }> {
    const params: Record<string, string | number> = { batchId };
    if (sessionDate) params["sessionDate"] = sessionDate;
    const response = await myAxios.get(
      `${this.baseUrl}/step/${stepId}/groups`,
      { params },
    );
    return response.data as any;
  }

  async deleteGroup(groupId: number): Promise<{ success: boolean }> {
    const response = await myAxios.delete(`${this.baseUrl}/groups/${groupId}`);
    return response.data as any;
  }

  // ========================================
  // Case Discussion Methods (AI Socratic Tutor)
  // ========================================

  async startCaseDiscussion(
    groupId: number,
    caseId: number,
  ): Promise<{
    success: boolean;
    data: {
      sessionId: number;
      groupId: number;
      caseId: number;
      case: { id: number; title: string; caseText: string; learningObjectives: string | null };
      turns: CaseDiscussionTurn[];
    };
  }> {
    const response = await myAxios.post(`${this.baseUrl}/case-discussion/start`, {
      groupId,
      caseId,
    });
    return response.data as any;
  }

  async getDiscussionTurns(
    sessionId: number,
    sinceTurnIdx?: number,
  ): Promise<{
    success: boolean;
    data: {
      sessionId: number;
      status: "active" | "closed";
      turnCount: number;
      turns: CaseDiscussionTurn[];
    };
  }> {
    const response = await myAxios.get(
      `${this.baseUrl}/case-discussion/${sessionId}/turns`,
      { params: { sinceTurnIdx: sinceTurnIdx ?? -1 } },
    );
    return response.data as any;
  }

  async respondInDiscussion(
    sessionId: number,
    content: string,
    actorEnquiryId?: number,
  ): Promise<{
    success: boolean;
    data: { sessionId: number; currentSignal: string; turns: CaseDiscussionTurn[] };
  }> {
    const response = await myAxios.post(
      `${this.baseUrl}/case-discussion/${sessionId}/respond`,
      { content, actorEnquiryId },
    );
    return response.data as any;
  }

  async teacherInject(sessionId: number, content: string): Promise<{ success: boolean }> {
    const response = await myAxios.post(
      `${this.baseUrl}/case-discussion/${sessionId}/teacher-inject`,
      { content },
    );
    return response.data as any;
  }

  async closeCaseDiscussion(sessionId: number): Promise<{ success: boolean }> {
    const response = await myAxios.post(
      `${this.baseUrl}/case-discussion/${sessionId}/close`,
    );
    return response.data as any;
  }

  async reopenCaseDiscussion(sessionId: number): Promise<{ success: boolean }> {
    const response = await myAxios.post(
      `${this.baseUrl}/case-discussion/${sessionId}/reopen`,
    );
    return response.data as any;
  }

  async getCaseDiscussionOverview(
    stepId: number,
    sessionDate: string,
    batchId: number,
  ): Promise<{
    success: boolean;
    data: CaseDiscussionOverview;
  }> {
    const response = await myAxios.get(
      `${this.baseUrl}/step/${stepId}/session/${sessionDate}/case-discussions/overview`,
      { params: { batchId } },
    );
    return response.data as any;
  }
}

export type GroupRole = "Solver" | "Explainer" | "Listener";

export interface SuggestedMember {
  userId: number;
  enrollmentId: number;
  name: string;
  preScore: number | null;
}

export interface SuggestedGroup {
  name: string;
  members: SuggestedMember[];
  avgPreScore: number | null;
}

export interface SessionGroupMember {
  id: number;
  userId: number;
  enrollmentId: number | null;
  name: string;
  role: GroupRole | null;
}

export interface SessionGroupRecord {
  id: number;
  name: string;
  createdAt: string;
  members: SessionGroupMember[];
}

export type DiscussionSignal =
  | "on_track"
  | "stuck"
  | "off_topic"
  | "key_insight"
  | "misconception";

export interface CaseDiscussionTurn {
  Id: number;
  SessionId: number;
  TurnIdx: number;
  ActorType: "ai" | "student" | "teacher";
  ActorEnquiryId: number | null;
  ActorUserId: number | null;
  Content: string;
  AIClassification: DiscussionSignal | null;
  AIClassificationReason: string | null;
  AIMisconceptionTag: string | null;
  CreatedAt: string;
}

export interface CaseDiscussionTile {
  sessionId: number;
  groupId: number;
  groupName: string;
  members: Array<{ userId: number; role: GroupRole | null }>;
  caseId: number;
  signal: DiscussionSignal;
  signalReason: string | null;
  misconceptionTag: string | null;
  turnCount: number;
  minutesSinceActivity: number;
  lastAiQuestion: string | null;
  lastStudentResponse: string | null;
  lastStudentTurnIdx: number | null;
  priority: number;
}

export interface WalkHereNext {
  groupId: number;
  groupName: string;
  signal: DiscussionSignal;
  signalReason: string | null;
  misconceptionTag: string | null;
  minutesSinceActivity: number;
  lastStudentResponse: string | null;
  suggestedProbe: string;
}

export interface CaseDiscussionOverview {
  walkHereNext: WalkHereNext | null;
  aggregate: {
    onTrack: number;
    stuck: number;
    offTopic: number;
    keyInsight: number;
    misconception: number;
  };
  misconceptionClusters: Array<{ tag: string; count: number }>;
  groups: CaseDiscussionTile[];
}

export default new FlipClassService();
